import { Component } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { INavItem } from '../../interfaces';
import { APP_ROUTES } from '../../enums';
import { TranslationService } from '../../services/translation.service';
import { getExplorisHealthUrl } from '../../utils/url.utils';
import { UserProfileService } from 'src/app/core/services/user-profile.service';

@Component({
  standalone: false,
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent {
  isCollapsed = false;
  getExplorisHealthUrl = getExplorisHealthUrl;

  navItems: INavItem[] = [
    { label: 'shared.components.sidebar.nav.home', route: APP_ROUTES.HOME, icon: 'assets/icons/home.svg' },
    { label: 'shared.components.sidebar.nav.test_results', route: APP_ROUTES.TESTS, icon: 'assets/icons/activity.svg' },
  ];

  /**
   * MVP-15 / MVP-17 / MVP-18 — Super User nav group. Rendered only when the
   * cached profile reports `isSuperUser === true`. One entry per SU child
   * route declared in `SuperUserRoutingModule` (`/su/tests`, `/su/users`,
   * `/su/sales-contact-requests`, `/su/export`).
   */
  superUserNavItems: INavItem[] = [
    {
      label: 'shared.components.sidebar.nav.superUser.tests',
      route: `${APP_ROUTES.SU}/tests`,
      icon: 'assets/icons/file.svg',
    },
    {
      label: 'shared.components.sidebar.nav.superUser.users',
      route: `${APP_ROUTES.SU}/users`,
      icon: 'assets/icons/users-profile.svg',
    },
    {
      label: 'shared.components.sidebar.nav.superUser.salesContactRequests',
      route: `${APP_ROUTES.SU}/sales-contact-requests`,
      icon: 'assets/images/email.svg',
    },
    {
      label: 'shared.components.sidebar.nav.superUser.export',
      route: `${APP_ROUTES.SU}/export`,
      icon: 'assets/icons/export.svg',
    },
    // MVP-39 — Phases page is hidden from the sidebar (and its route removed
    // in `SuperUserRoutingModule`). The i18n key `superUser.phases` is kept
    // for harmless backwards-compat in case the page returns later.
    // Phase Renewals merged into Sales Contact Requests — the unified queue
    // surfaces both flows with inline Approve/Decline. Route still exists
    // for backwards compatibility but is no longer linked from the sidebar.
  ];

  /**
   * MVP-15 — async flag for the `*ngIf` on the SU nav group.
   * Reads the already-cached profile (LayoutComponent fetches it on init),
   * so this is effectively synchronous in steady state.
   */
  isSuperUser$: Observable<boolean> = this.userProfileService
    .getProfileObservable()
    .pipe(map(profile => profile?.isSuperUser === true));

  secondaryNavItems = [
    {
      label: 'shared.components.sidebar.nav.settings',
      route: APP_ROUTES.SETTINGS,
      icon: 'assets/icons/settings.svg',
      isExternal: false,
    },
    {
      label: 'shared.components.sidebar.nav.contact_us',
      route: '#contact',
      icon: 'assets/images/email.svg',
      isExternal: true,
    },
    {
      label: 'shared.components.sidebar.nav.help',
      route: '#resources',
      icon: 'assets/icons/help.svg',
      isExternal: true,
    },
  ];

  get currentLang(): string {
    return this.translationService.getCurrentLang();
  }

  constructor(
    private translationService: TranslationService,
    private userProfileService: UserProfileService
  ) {}

  /**
   * @description Toggles sidebar collapse state.
   */
  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
  }
}
